import { cn } from '@/lib/utils';
import {
    type UseFluidHoverReturn,
    useFluidHover,
    useRegisterFluidHoverItem
} from '@/hooks/use-fluid-hover';
import * as React from 'react';

import { FluidHoverHighlight } from '@/components/fluid-hover-highlight';

interface FluidHoverGroupContextValue {
    registerItem: UseFluidHoverReturn['registerItem'];
    claimIndex: () => number;
}

const FluidHoverGroupContext =
    React.createContext<FluidHoverGroupContextValue | null>(null);

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
    if (typeof ref === 'function') ref(value);
    else if (ref) (ref as React.MutableRefObject<T | null>).current = value;
}

interface FluidHoverGroupProps extends React.HTMLAttributes<HTMLDivElement> {
    axis?: 'x' | 'y' | 'xy';
    highlightClassName?: string;
}

const FluidHoverGroup = React.forwardRef<HTMLDivElement, FluidHoverGroupProps>(
    (
        {
            className,
            children,
            axis = 'y',
            highlightClassName,
            onMouseEnter,
            onMouseMove,
            onMouseLeave,
            onClick,
            ...props
        },
        forwardedRef
    ) => {
        const containerRef = React.useRef<HTMLDivElement>(null);
        const hover = useFluidHover(containerRef, { axis, gapClick: false });
        const nextIndex = React.useRef(0);

        const setRef = React.useCallback(
            (node: HTMLDivElement | null) => {
                (
                    containerRef as React.MutableRefObject<HTMLDivElement | null>
                ).current = node;
                assignRef(forwardedRef, node);
            },
            [forwardedRef]
        );

        const value = React.useMemo<FluidHoverGroupContextValue>(
            () => ({
                registerItem: hover.registerItem,
                claimIndex: () => nextIndex.current++
            }),
            [hover.registerItem]
        );

        return (
            <FluidHoverGroupContext.Provider value={value}>
                <div
                    {...props}
                    ref={setRef}
                    className={cn('relative', className)}
                    onMouseEnter={(event) => {
                        onMouseEnter?.(event);
                        hover.handlers.onMouseEnter();
                    }}
                    onMouseMove={(event) => {
                        onMouseMove?.(event);
                        hover.handlers.onMouseMove(event);
                    }}
                    onMouseLeave={(event) => {
                        onMouseLeave?.(event);
                        hover.handlers.onMouseLeave();
                    }}
                    onClick={(event) => {
                        onClick?.(event);
                        hover.handlers.onClick(event);
                    }}
                >
                    <FluidHoverHighlight
                        hover={hover}
                        className={cn('z-40 rounded-lg', highlightClassName)}
                    />
                    {children}
                </div>
            </FluidHoverGroupContext.Provider>
        );
    }
);
FluidHoverGroup.displayName = 'FluidHoverGroup';

function useInFluidHoverGroup() {
    return React.useContext(FluidHoverGroupContext) !== null;
}

function useFluidHoverGroupItem<T extends HTMLElement>(
    forwardedRef: React.Ref<T> | undefined,
    enabled = true
) {
    const group = React.useContext(FluidHoverGroupContext);
    const itemRef = React.useRef<T | null>(null);
    const [index] = React.useState(() => group?.claimIndex());
    useRegisterFluidHoverItem(
        enabled ? group?.registerItem : undefined,
        index,
        itemRef
    );

    return React.useCallback(
        (node: T | null) => {
            itemRef.current = node;
            assignRef(forwardedRef, node);
        },
        [forwardedRef]
    );
}

export { FluidHoverGroup, useFluidHoverGroupItem, useInFluidHoverGroup };
